import type { AssetManager } from '../render/AssetManager';

/**
 * Web Audio playback: one music channel, two ambiance slots (each with two
 * flip-flopping tracks, GDKnyttAmbiChannel), and SFX (one-shot + looping
 * movement sounds, with optional 2D panning like AudioStreamPlayer2D).
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private bufferCache = new Map<string, Promise<AudioBuffer | null>>();

  private ambiance: (AmbiSlot | null)[] = [null, null];
  private loops = new Map<string, { src: AudioBufferSourceNode; gain: GainNode }>();
  // Muffle zone (bank 7,17): Juni.cs doMuffle sets the movement players to -10 dB
  // muffled / +5 dB unmuffled (scene default 0 dB until the first muffle toggle).
  private movementDuckDb = 0;

  musicVolume = 0.7;
  ambianceVolume = 0.8;
  sfxVolume = 0.9;
  // Honour a persisted mute so audio is silent from the very first note (before
  // unlock/start), and the choice survives reloads.
  muted = AudioManager.readMutedPref();

  constructor(
    private assets: AssetManager,
    // GDKnyttAssetManager.isSongLooped: Song{N}.ogg loops only if the world INI
    // has [Loop Music] N=true. Default keeps non-Game constructions working.
    private isSongLooped: (num: number) => boolean = () => true,
  ) {}

  private static readonly MUTE_KEY = 'knytt-muted';
  private static readMutedPref(): boolean {
    try {
      return globalThis.localStorage?.getItem(AudioManager.MUTE_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Must be called from a user gesture to satisfy autoplay policies. */
  unlock(): void {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 1;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : 1;
    try {
      globalThis.localStorage?.setItem(AudioManager.MUTE_KEY, m ? '1' : '0');
    } catch {
      /* localStorage unavailable */
    }
  }

  private async loadBuffer(path: string): Promise<AudioBuffer | null> {
    let p = this.bufferCache.get(path);
    if (!p) {
      p = (async () => {
        const bytes = await this.assets.resolveBytes(path);
        if (!bytes || !this.ctx) return null;
        try {
          // copy into a fresh ArrayBuffer (decodeAudioData detaches it)
          const ab = bytes.slice().buffer;
          return await this.ctx.decodeAudioData(ab);
        } catch {
          return null;
        }
      })();
      this.bufferCache.set(path, p);
    }
    return p;
  }

  // ---- Music ----
  // GDKnyttMusicChannel: a playing song fades out over 5s, and only then does the
  // queued track start, fading in over 2s (sequential, not a crossfade). Each track
  // first plays Music/Intro{N}.ogg once (if present), then chains into Song{N}.ogg,
  // which loops only when the world's [Loop Music] flag says so; a finished
  // non-looping song stays silent and is NOT restarted by a same-track request.
  private static readonly MUSIC_FADE_OUT = 5;
  private static readonly MUSIC_FADE_IN = 2;
  private musicTrack = 0; // TrackNumber — kept even after a non-looping song ends
  private musicCh: Channel | null = null;
  private musicIsIntro = false;
  private musicQueued: number | null = null;
  private musicNoFadeIn = false;
  private musicFadingOut = false;
  private musicFadeTimer: ReturnType<typeof setTimeout> | null = null;
  private musicToken = 0;

  setMusic(num: number, noFadeIn = false): void {
    if (!this.ctx) return;
    this.musicNoFadeIn = noFadeIn;
    if (num === this.musicTrack) {
      // Same track: drop any queued change (GDKnyttMusicChannel.clearQ); if it is
      // mid-fade-out, reverse the fade in place — same stream, same position.
      this.musicQueued = null;
      if (noFadeIn) return;
      if (this.musicFadingOut && this.musicCh) {
        this.clearMusicFadeTimer();
        this.musicFadingOut = false;
        const frac = this.musicCh.gainFraction(this.musicVolume);
        this.musicCh.fadeTo(this.musicVolume, AudioManager.MUSIC_FADE_OUT * (1 - frac));
      }
      return;
    }
    // Different track: swap the queue no matter what (changeTrack/setQ).
    this.musicQueued = num;
    if (this.musicFadingOut) return; // already fading out — just changed the queue
    if (this.musicCh?.playing) {
      // Actively playing: fade out, then start the queued track.
      this.musicFadingOut = true;
      this.musicCh.fadeTo(0, AudioManager.MUSIC_FADE_OUT);
      this.musicFadeTimer = setTimeout(() => {
        this.musicFadeTimer = null;
        this.playQueued();
      }, AudioManager.MUSIC_FADE_OUT * 1000);
      return;
    }
    this.playQueued(); // nothing audible — start immediately
  }

  private clearMusicFadeTimer(): void {
    if (this.musicFadeTimer != null) {
      clearTimeout(this.musicFadeTimer);
      this.musicFadeTimer = null;
    }
  }

  /**
   * Start the queued track now (GDKnyttMusicChannel.playQueued). Called when a
   * fade-out completes, when an intro/non-looping song finishes, and by Shift
   * StopMusic hard-cuts. With no queue: chains a finished intro into its song,
   * otherwise does nothing (a finished non-looping song stays finished).
   */
  playQueued(): void {
    if (!this.ctx) return;
    if (this.musicQueued == null && !this.musicIsIntro) return;
    this.clearMusicFadeTimer();
    this.musicFadingOut = false;
    const token = ++this.musicToken; // invalidates the old channel's onEnded
    this.musicCh?.stop(0);
    this.musicCh = null;

    let withIntro: boolean;
    if (this.musicQueued != null) {
      this.musicTrack = this.musicQueued;
      this.musicQueued = null;
      withIntro = true;
    } else {
      withIntro = false; // intro finished — chain into the song proper
    }
    this.musicIsIntro = false;
    const num = this.musicTrack;
    if (num === 0) return;
    const ch = new Channel(this.ctx, this.master, num, this.musicVolume);
    this.musicCh = ch;
    void (async () => {
      const intro = withIntro ? await this.loadBuffer(`Music/Intro${num}.ogg`) : null;
      const buf = intro ?? (await this.loadBuffer(`Music/Song${num}.ogg`));
      if (token !== this.musicToken || this.musicCh !== ch || !buf) return;
      this.musicIsIntro = !!intro;
      ch.onEnded = (): void => {
        if (token === this.musicToken) this.playQueued();
      };
      ch.play(buf, intro ? false : this.isSongLooped(num), this.musicNoFadeIn ? 0 : AudioManager.MUSIC_FADE_IN);
    })();
  }

  /** Shift StopMusic re-fade (GDKnyttMusicChannel.fadeIn): re-ramp the current track to full. */
  fadeInMusic(sec: number): void {
    this.clearMusicFadeTimer();
    this.musicFadingOut = false;
    this.musicCh?.fadeTo(this.musicVolume, sec);
  }

  // ---- Ambiance ----
  /**
   * Set an ambiance slot's track (GDKnyttAmbiChannel.setTrack). Each slot holds two
   * tracks that flip on change, so an A→B→A toggle within the ~1.25s fade resumes
   * the still-playing stream at its position, fading back from its current level.
   * `customVolume` (LocalAmbient/MuteAmbient) starts the track muted and drives its
   * gain per-frame via requestAmbianceVolume + commitAmbiance.
   */
  setAmbiance(slot: number, num: number, customVolume = false): void {
    if (!this.ctx) return;
    let s = this.ambiance[slot];
    if (!s) {
      s = new AmbiSlot(this.ctx, this.master);
      this.ambiance[slot] = s;
    }
    s.setTrack(num, customVolume, this.ambianceVolume, (n) => this.loadBuffer(`Ambiance/Ambi${n}.ogg`));
  }

  /** LocalAmbient: request a dB level for a custom-volume ambiance slot this frame. */
  requestAmbianceVolume(slot: number, db: number): void {
    this.ambiance[slot]?.requestDb(db);
  }

  /** Apply per-frame custom ambiance volumes; call once per frame after object updates. */
  commitAmbiance(): void {
    this.ambiance[0]?.commitVolume(this.ambianceVolume);
    this.ambiance[1]?.commitVolume(this.ambianceVolume);
  }

  /** Shift cutscene (Shift.cs:176-180): snap both ambiance slots' fades to their target. */
  stopAmbianceFade(): void {
    this.ambiance.forEach((s) => s?.stopFade(this.ambianceVolume));
  }

  /**
   * Load an SFX buffer by base name. The bundled bank ships as .flac (tried
   * first — zero-waste for the common case); custom worlds can still override
   * a name with their own .wav or .ogg, which resolveBytes() checks the
   * world's own archive for before ever reaching the network.
   */
  private async loadSfx(name: string): Promise<AudioBuffer | null> {
    return (
      (await this.loadBuffer(`Sfx/${name}.flac`)) ??
      (await this.loadBuffer(`Sfx/${name}.wav`)) ??
      (await this.loadBuffer(`Sfx/${name}.ogg`))
    );
  }

  // ---- SFX ----
  // Object SFX are AudioStreamPlayer2Ds in yknytt: panned across the screen and
  // mildly attenuated by distance (default panning strength 1, max_distance 2000).
  private static readonly SFX_MAX_DIST = 2000;

  /**
   * `movement` marks a Juni footstep/climb/slide/jump/land cue, ducked by Muffle
   * zones. `at` (screen px, 600x240 view) pans/attenuates like AudioStreamPlayer2D;
   * omit it for centred playback (Juni's own sounds, UI).
   */
  sfx(name: string, movement = false, at?: { x: number; y: number }): void {
    if (!this.ctx) return;
    void this.loadSfx(name).then((buf) => {
      if (!buf || !this.ctx) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const g = this.ctx.createGain();
      g.gain.value = this.sfxVolume * (movement ? Math.pow(10, this.movementDuckDb / 20) : 1);
      this.connectPanned(src, g, at);
      src.start();
    });
  }

  /** One-shot play of a buffer at an explicit world-relative path (e.g. custom Sounds/x.ogg). */
  sfxFromPath(path: string): void {
    if (!this.ctx) return;
    void this.loadBuffer(path).then((buf) => {
      if (!buf || !this.ctx) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const g = this.ctx.createGain();
      g.gain.value = this.sfxVolume;
      src.connect(g).connect(this.master);
      src.start();
    });
  }

  /** Route src→gain→master, inserting a stereo pan + distance attenuation for `at`. */
  private connectPanned(src: AudioBufferSourceNode, g: GainNode, at?: { x: number; y: number }): void {
    if (at && typeof this.ctx!.createStereoPanner === 'function') {
      // Godot pans by horizontal screen position: -1 at the left edge, +1 at the right.
      const pan = Math.max(-1, Math.min(1, (at.x / 300) - 1));
      const dist = Math.hypot(at.x - 300, at.y - 120);
      g.gain.value *= Math.max(0, 1 - dist / AudioManager.SFX_MAX_DIST);
      const p = this.ctx!.createStereoPanner();
      p.pan.value = pan;
      src.connect(g).connect(p).connect(this.master);
    } else {
      src.connect(g).connect(this.master);
    }
  }

  startLoop(name: string, movement = false): void {
    if (!this.ctx || this.loops.has(name)) return;
    void this.loadSfx(name).then((buf) => {
      if (!buf || !this.ctx || this.loops.has(name)) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const g = this.ctx.createGain();
      g.gain.value = this.sfxVolume * (movement ? Math.pow(10, this.movementDuckDb / 20) : 1);
      src.connect(g).connect(this.master);
      src.start();
      this.loops.set(name, { src, gain: g });
    });
  }

  stopLoop(name: string): void {
    const loop = this.loops.get(name);
    if (loop) {
      try {
        loop.src.stop();
      } catch {
        /* already stopped */
      }
      this.loops.delete(name);
    }
  }

  /**
   * Mid-game cutscene: C# detaches the whole game scene, so its music/ambiance/SFX
   * go silent and resume in place afterwards. Suspending the context is the Web
   * Audio equivalent (streams freeze at their position).
   */
  suspendForCutscene(): void {
    if (this.ctx?.state === 'running') void this.ctx.suspend();
  }
  resumeFromCutscene(): void {
    if (this.ctx?.state === 'suspended') void this.ctx.resume();
  }

  /** Win (Juni.cs:813 stopAll + game teardown): hard-stop music, ambiance and loops. */
  stopAllGame(): void {
    this.musicToken++;
    this.clearMusicFadeTimer();
    this.musicQueued = null;
    this.musicCh?.stop(0);
    this.musicCh = null;
    this.musicTrack = 0;
    this.ambiance.forEach((s) => s?.destroy());
    this.ambiance = [null, null];
    for (const name of [...this.loops.keys()]) this.stopLoop(name);
  }

  /** Muffle zone (bank 7,17): duck (or restore) all movement SFX (Juni.cs -10/+5 dB). */
  setMovementMuffle(muffled: boolean): void {
    this.movementDuckDb = muffled ? -10 : 5;
    const mult = this.sfxVolume * Math.pow(10, this.movementDuckDb / 20);
    for (const { gain } of this.loops.values()) gain.gain.value = mult;
  }

  destroy(): void {
    this.musicToken++;
    this.clearMusicFadeTimer();
    this.musicCh?.stop(0);
    this.musicCh = null;
    this.ambiance.forEach((s) => s?.destroy());
    for (const [, loop] of this.loops) {
      try {
        loop.src.stop();
      } catch {
        /* noop */
      }
    }
    this.loops.clear();
    void this.ctx?.close();
    this.ctx = null;
  }
}

/** A single fading music voice. */
class Channel {
  private src: AudioBufferSourceNode | null = null;
  private gain: GainNode;
  private stopped = false;
  private ended = false;
  private started = false;
  onEnded: (() => void) | null = null;

  constructor(
    private ctx: AudioContext,
    dest: AudioNode,
    public readonly num: number,
    private volume: number,
  ) {
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.gain.connect(dest);
  }

  /** Whether the stream is (or will shortly be) audibly running. */
  get playing(): boolean {
    return !this.stopped && !this.ended;
  }

  play(buffer: AudioBuffer, loop: boolean, fade: number): void {
    if (this.stopped) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = loop;
    src.connect(this.gain);
    src.onended = (): void => {
      if (this.stopped) return;
      this.ended = true;
      this.onEnded?.();
    };
    const t = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(t);
    if (fade > 0) {
      this.gain.gain.setValueAtTime(0, t);
      this.gain.gain.linearRampToValueAtTime(this.volume, t + fade);
    } else {
      this.gain.gain.setValueAtTime(this.volume, t);
    }
    src.start();
    this.src = src;
    this.started = true;
  }

  /** Current gain as a fraction of full volume (for fade reversal). */
  gainFraction(vol: number): number {
    return vol > 0 ? Math.max(0, Math.min(1, this.gain.gain.value / vol)) : 0;
  }

  /** Ramp gain from its current value to `target` over `sec` (fade in or out). */
  fadeTo(target: number, sec: number): void {
    if (this.stopped) return;
    const t = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(t);
    this.gain.gain.setValueAtTime(this.gain.gain.value, t);
    if (sec > 0) this.gain.gain.linearRampToValueAtTime(target, t + sec);
    else this.gain.gain.setValueAtTime(target, t);
  }

  stop(fade: number): void {
    if (this.stopped) return;
    this.stopped = true;
    this.onEnded = null;
    const t = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(t);
    this.gain.gain.setValueAtTime(this.gain.gain.value, t);
    this.gain.gain.linearRampToValueAtTime(0, t + fade);
    const src = this.src;
    if (src && this.started) {
      try {
        src.stop(t + fade + 0.05);
      } catch {
        /* noop */
      }
    }
  }
}

// GDKnyttAmbiTrack: fades run in dB, lerp(-15, 0, i) with i moving at 0.8/s
// (≈1.25s full fade); i==0 hard-stops the stream (unless muting).
const AMBI_FADE = 1.25;
const AMBI_FLOOR_DB = -15;

/** One of an ambiance slot's two flip-flopping tracks (GDKnyttAmbiTrack). */
class AmbiTrack {
  num = 0;
  customVolume = false;
  private src: AudioBufferSourceNode | null = null;
  private gain: GainNode;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private token = 0;
  private fadeTarget: 'in' | 'out' | null = null;
  private muting = false;
  private pendingDb = -100;

  constructor(
    private ctx: AudioContext,
    dest: AudioNode,
  ) {
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.gain.connect(dest);
  }

  /** Start a fresh stream from position 0 (changeTrack). */
  changeTrack(num: number, loadBuf: (n: number) => Promise<AudioBuffer | null>, volume: number, custom: boolean): void {
    this.clearStopTimer();
    this.stopNow();
    this.num = num;
    this.customVolume = custom;
    this.fadeTarget = null;
    if (num === 0) return;
    const token = ++this.token;
    void loadBuf(num).then((buf) => {
      if (token !== this.token || !buf) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(this.gain);
      const t = this.ctx.currentTime;
      this.gain.gain.cancelScheduledValues(t);
      if (custom) {
        this.gain.gain.setValueAtTime(0, t); // driven per-frame by commitVolume
      } else {
        // fade_i starts at 0 → -15 dB, ramping to full over 1.25s.
        this.fadeTarget = 'in';
        this.gain.gain.setValueAtTime(volume * Math.pow(10, AMBI_FLOOR_DB / 20), t);
        this.gain.gain.linearRampToValueAtTime(volume, t + AMBI_FADE);
      }
      src.start();
      this.src = src;
    });
  }

  /** ≈ InverseLerp(-15, 0, VolumeDb): how far through the -15dB..0dB fade band we are. */
  private fadeFraction(volume: number): number {
    const g = this.gain.gain.value;
    const floor = volume * Math.pow(10, AMBI_FLOOR_DB / 20);
    if (g <= floor) return 0;
    if (g >= volume) return 1;
    const db = 20 * Math.log10(g / volume);
    return Math.max(0, Math.min(1, (db - AMBI_FLOOR_DB) / -AMBI_FLOOR_DB));
  }

  /** "Smart" fade-in: resume from the current level (cancels a pending stop). */
  fadeIn(volume: number): void {
    if (!this.src) return;
    this.clearStopTimer();
    this.customVolume = false;
    this.muting = false;
    this.fadeTarget = 'in';
    const i = this.fadeFraction(volume);
    const t = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(t);
    this.gain.gain.setValueAtTime(this.gain.gain.value, t);
    this.gain.gain.linearRampToValueAtTime(volume, t + AMBI_FADE * (1 - i));
  }

  /** Fade down from the current level; hard-stop at the end unless `mute`. */
  fadeOut(volume: number, mute = false): void {
    if (!this.src) {
      if (!mute) this.num = 0;
      return;
    }
    this.fadeTarget = 'out';
    this.muting = mute;
    const i = this.fadeFraction(volume);
    const dur = AMBI_FADE * i;
    const t = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(t);
    this.gain.gain.setValueAtTime(this.gain.gain.value, t);
    this.gain.gain.linearRampToValueAtTime(volume * Math.pow(10, AMBI_FLOOR_DB / 20), t + dur);
    this.gain.gain.setValueAtTime(0, t + dur);
    this.clearStopTimer();
    this.stopTimer = setTimeout(
      () => {
        this.stopTimer = null;
        this.fadeTarget = null;
        this.muting = false;
        if (mute) return; // keep playing silently (entering a custom-volume area)
        this.stopNow();
        this.num = 0;
      },
      dur * 1000 + 30,
    );
  }

  /** Immediate silence, stream keeps playing (custom-volume mute). */
  mute(): void {
    this.clearStopTimer();
    this.fadeTarget = null;
    const t = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(t);
    this.gain.gain.setValueAtTime(0, t);
  }

  /** Snap an in-progress fade to its target (AmbiTrack.stopFade: fade_i = fade_target). */
  stopFade(volume: number): void {
    if (this.fadeTarget === 'in') {
      const t = this.ctx.currentTime;
      this.gain.gain.cancelScheduledValues(t);
      this.gain.gain.setValueAtTime(volume, t);
      this.fadeTarget = null;
    } else if (this.fadeTarget === 'out') {
      this.clearStopTimer();
      this.fadeTarget = null;
      const t = this.ctx.currentTime;
      this.gain.gain.cancelScheduledValues(t);
      this.gain.gain.setValueAtTime(0, t);
      if (!this.muting) {
        this.stopNow();
        this.num = 0;
      }
      this.muting = false;
    }
  }

  /** LocalAmbient: request a dB level this frame (the max across requests wins). */
  requestDb(db: number): void {
    if (db > this.pendingDb) this.pendingDb = db;
  }

  /** Apply the requested dB (or mute) for custom-volume tracks; reset for next frame. */
  commitVolume(fullVolume: number): void {
    if (!this.customVolume) return;
    const lin = this.pendingDb <= -100 ? 0 : fullVolume * Math.pow(10, this.pendingDb / 20);
    this.gain.gain.value = lin;
    this.pendingDb = -100;
  }

  private clearStopTimer(): void {
    if (this.stopTimer != null) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
  }

  private stopNow(): void {
    this.token++;
    if (this.src) {
      try {
        this.src.stop();
      } catch {
        /* noop */
      }
      this.src = null;
    }
  }

  destroy(): void {
    this.clearStopTimer();
    this.stopNow();
    this.num = 0;
  }
}

/** An ambiance slot: two tracks flipped on change (GDKnyttAmbiChannel). */
class AmbiSlot {
  private t1: AmbiTrack;
  private t2: AmbiTrack;
  private on1 = true;
  private hasCustom = false;

  constructor(ctx: AudioContext, dest: AudioNode) {
    this.t1 = new AmbiTrack(ctx, dest);
    this.t2 = new AmbiTrack(ctx, dest);
  }

  private get cur(): AmbiTrack {
    return this.on1 ? this.t1 : this.t2;
  }

  setTrack(num: number, custom: boolean, volume: number, loadBuf: (n: number) => Promise<AudioBuffer | null>): void {
    const cur = this.cur;
    // Same track across a screen change: only the custom-volume mode transitions.
    if (cur.num === num && this.hasCustom && !custom) cur.fadeIn(volume); // leaving a muted area
    if (cur.num === num && custom) {
      cur.fadeOut(volume, true); // entering one: fade to silent but keep playing
      cur.customVolume = true;
    }
    this.hasCustom = custom;
    if (cur.num === num) return;

    // Fade out the outgoing track (it stays resumable until the fade completes).
    if (cur.num !== 0) cur.fadeOut(volume);
    this.on1 = !this.on1;
    const next = this.cur;
    if (next.num === num && num !== 0) {
      next.fadeIn(volume); // A→B→A within the fade: resume the still-playing stream
    } else {
      next.changeTrack(num, loadBuf, volume, custom);
    }
    if (custom) {
      next.mute();
      next.customVolume = true;
    }
  }

  requestDb(db: number): void {
    this.cur.requestDb(db);
  }

  commitVolume(fullVolume: number): void {
    this.cur.commitVolume(fullVolume);
  }

  stopFade(volume: number): void {
    this.t1.stopFade(volume);
    this.t2.stopFade(volume);
  }

  destroy(): void {
    this.t1.destroy();
    this.t2.destroy();
  }
}
